/**
 * Scenario arithmetic: what a flat of a given size is worth in an area today,
 * and what it might be worth in N years.
 *
 * Deliberately free of React and of any network access — everything here is a
 * pure function over a price time series. That keeps the model inspectable and
 * leaves room for the financing side (loan, notary fees, rental yield) to be
 * layered on later as more pure functions over the same shapes.
 */

import type { YearStat } from "./api";

/** Below this a year's median is suppressed upstream and cannot be trusted. */
const MIN_SALES = 5;

/** How far the optimistic and pessimistic edges sit from the central rate. */
export const DEFAULT_SPREAD = 0.015;

export interface GrowthFit {
  /** Compound annual rate implied by the recorded years, as a fraction. */
  rate: number;
  fromYear: number;
  toYear: number;
  /** How many yearly medians the fit is based on. */
  points: number;
}

const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

/** Years with enough sales behind them to be worth reading. */
export const usableYears = (years: YearStat[]): YearStat[] =>
  years.filter((y) => y.median_m2 > 0 && y.n >= MIN_SALES);

/**
 * The area's own historical growth rate.
 *
 * A least-squares fit through log(median) rather than a plain first-to-last
 * CAGR: with only five yearly points, one thin or unusual year at either end
 * would otherwise set the whole trend on its own.
 */
export function fitGrowth(years: YearStat[]): GrowthFit | null {
  const pts = usableYears(years);
  if (pts.length < 2) return null;

  const mx = mean(pts.map((p) => p.year));
  const my = mean(pts.map((p) => Math.log(p.median_m2)));
  let num = 0;
  let den = 0;
  for (const p of pts) {
    const dx = p.year - mx;
    num += dx * (Math.log(p.median_m2) - my);
    den += dx * dx;
  }
  if (den === 0) return null;

  return {
    rate: Math.exp(num / den) - 1,
    fromYear: pts[0].year,
    toYear: pts[pts.length - 1].year,
    points: pts.length,
  };
}

/** The most recent year whose median is publishable. */
export function baseYear(years: YearStat[]): YearStat | null {
  const pts = usableYears(years);
  return pts.length ? pts[pts.length - 1] : null;
}

export interface Estimate {
  year: number;
  /** €/m² the estimate is built from. */
  perM2: number;
  value: number;
  /** Middle half of comparable sales, at this surface. */
  low: number;
  high: number;
  /** Typical surface actually sold in this area, for a sanity check. */
  typicalSurface: number;
  sales: number;
}

/** What a flat of `surface` m² costs in this area today, at market medians. */
export function estimate(years: YearStat[], surface: number): Estimate | null {
  const base = baseYear(years);
  if (!base || surface <= 0) return null;
  return {
    year: base.year,
    perM2: base.median_m2,
    value: base.median_m2 * surface,
    low: base.p25_m2 * surface,
    high: base.p75_m2 * surface,
    typicalSurface: base.median_surface,
    sales: base.n,
  };
}

export const compound = (base: number, rate: number, years: number): number =>
  base * Math.pow(1 + rate, years);

export interface ProjectionRow {
  year: number;
  /** Recorded median at this surface — null once past the last known year. */
  actual: number | null;
  /** Middle half of recorded sales at this surface. */
  actualRange: [number, number] | null;
  /** Central scenario — null before the base year. */
  projected: number | null;
  /** Pessimistic-to-optimistic span around the central scenario. */
  band: [number, number] | null;
}

export interface Projection {
  rows: ProjectionRow[];
  base: Estimate;
  /** Value at the end of the horizon, central scenario. */
  final: number;
  finalLow: number;
  finalHigh: number;
  finalYear: number;
}

/**
 * History and forward scenario in one series.
 *
 * The projection starts *at* the base year rather than after it, so the dashed
 * forward line begins exactly where the recorded line ends instead of leaving
 * a one-year gap in the chart.
 */
export function project(
  years: YearStat[],
  surface: number,
  rate: number,
  horizon: number,
  spread: number = DEFAULT_SPREAD,
): Projection | null {
  const base = estimate(years, surface);
  if (!base) return null;

  const rows: ProjectionRow[] = usableYears(years).map((y) => ({
    year: y.year,
    actual: y.median_m2 * surface,
    actualRange: [y.p25_m2 * surface, y.p75_m2 * surface],
    projected: null,
    band: null,
  }));

  for (let t = 0; t <= horizon; t++) {
    const year = base.year + t;
    const central = compound(base.value, rate, t);
    const band: [number, number] = [
      compound(base.value, rate - spread, t),
      compound(base.value, rate + spread, t),
    ];
    const existing = rows.find((r) => r.year === year);
    if (existing) {
      existing.projected = central;
      existing.band = band;
    } else {
      rows.push({ year, actual: null, actualRange: null, projected: central, band });
    }
  }

  rows.sort((a, b) => a.year - b.year);
  const last = rows[rows.length - 1];
  return {
    rows,
    base,
    final: last.projected ?? base.value,
    finalLow: last.band?.[0] ?? base.value,
    finalHigh: last.band?.[1] ?? base.value,
    finalYear: last.year,
  };
}
